import { baseTaxRates, gewerbesteuerData, entityTypes } from '../data/taxEntityConfig';
import { standardAfATiers, degressiveAfA, denkmalschutzAfA } from '../data/depreciationConfig';

// German Einkommensteuer (progressive income tax) calculation for 2026
// Uses the official bracket formulas from §32a EStG
export function calculateEinkommensteuer(taxableIncome) {
  if (taxableIncome <= 0) return 0;
  const x = Math.floor(taxableIncome);

  // Zone 1: Grundfreibetrag
  if (x <= 12348) return 0;

  // Zone 2: First progressive zone (14% to ~24%)
  if (x <= 17799) {
    const y = (x - 12348) / 10000;
    return Math.floor((914.51 * y + 1400) * y);
  }

  // Zone 3: Second progressive zone (~24% to 42%)
  if (x <= 69878) {
    const z = (x - 17799) / 10000;
    return Math.floor((173.10 * z + 2397) * z + 1034.87);
  }

  // Zone 4: Flat 42%
  if (x <= 277825) {
    return Math.floor(0.42 * x - 11135.63);
  }

  // Zone 5: Reichensteuer 45%
  return Math.floor(0.45 * x - 19470.38);
}

// Solidaritätszuschlag for Einkommensteuer (2024+ rules, §4 SolzG)
export function calculateSolidaritaetszuschlag(einkommensteuer) {
  if (einkommensteuer <= 0) return 0;

  const freigrenze = 18130; // Freigrenze 2024+
  if (einkommensteuer <= freigrenze) return 0;

  const fullSoli = einkommensteuer * 0.055;

  // Milderungszone: marginal rate 11.9% of amount above Freigrenze
  const milderungsSoli = (einkommensteuer - freigrenze) * 0.119;

  return Math.min(fullSoli, milderungsSoli);
}

// Kirchensteuer: 8% (Bayern/BW) or 9% (elsewhere) of Einkommensteuer
// Only applies to natural persons (private, gmbh_co_kg)
export function calculateKirchensteuer(einkommensteuer, rate = 0.09) {
  if (einkommensteuer <= 0) return 0;
  return einkommensteuer * rate;
}

// Corporate tax: KSt + Soli + GewSt
export function calculateCorporateTax(entityType, taxableIncome, hebesatz = 410) {
  if (taxableIncome <= 0) return { kst: 0, soli: 0, gewst: 0, total: 0, rate: 0 };

  const kst = taxableIncome * baseTaxRates.koerperschaftsteuer;
  const soli = kst * baseTaxRates.solidaritaetszuschlag;

  let gewst = 0;
  const entity = entityTypes[entityType];
  if (entity && entity.gewerbesteuer) {
    gewst = taxableIncome * gewerbesteuerData.steuermesszahl * (hebesatz / 100);
  }

  const total = kst + soli + gewst;
  const rate = taxableIncome > 0 ? total / taxableIncome : 0;

  return { kst, soli, gewst, total, rate };
}

// Calculate effective tax rate for any entity type
export function calculateTaxRate(entityType, taxableIncome, personalTaxRate = 0.42, hebesatz = 410, kirchensteuerRate = 0) {
  if (taxableIncome <= 0) return 0;

  const entity = entityTypes[entityType];
  if (!entity) return 0.42;

  switch (entityType) {
    case 'private':
    case 'gmbh_co_kg': {
      const est = calculateEinkommensteuer(taxableIncome);
      const soli = calculateSolidaritaetszuschlag(est);
      const kist = kirchensteuerRate > 0 ? calculateKirchensteuer(est, kirchensteuerRate) : 0;
      return (est + soli + kist) / taxableIncome;
    }
    case 'gmbh': {
      const result = calculateCorporateTax('gmbh', taxableIncome, hebesatz);
      return result.rate;
    }
    case 'immo_gmbh':
    case 'stiftung': {
      return baseTaxRates.kstPlusSoli; // 15.825%, no GewSt
    }
    default:
      return personalTaxRate;
  }
}

// Calculate tax amount for an entity
export function calculateTaxAmount(entityType, taxableIncome, personalTaxRate = 0.42, hebesatz = 410, kirchensteuerRate = 0, otherIncome = 0) {
  if (taxableIncome <= 0) return 0;

  switch (entityType) {
    case 'private':
    case 'gmbh_co_kg': {
      // Marginal tax: tax on (property + other) minus tax on (other alone)
      const totalIncome = taxableIncome + otherIncome;
      const estTotal = calculateEinkommensteuer(totalIncome);
      const estOtherOnly = otherIncome > 0 ? calculateEinkommensteuer(otherIncome) : 0;
      const estProperty = estTotal - estOtherOnly;
      const soli = calculateSolidaritaetszuschlag(estProperty);
      const kist = kirchensteuerRate > 0 ? calculateKirchensteuer(estProperty, kirchensteuerRate) : 0;
      return estProperty + soli + kist;
    }
    case 'gmbh': {
      return calculateCorporateTax('gmbh', taxableIncome, hebesatz).total;
    }
    case 'immo_gmbh':
    case 'stiftung': {
      return taxableIncome * baseTaxRates.kstPlusSoli;
    }
    default:
      return taxableIncome * personalTaxRate;
  }
}

// Spekulationssteuer (capital gains tax on property sale)
export function calculateSpekulationssteuer(entityType, capitalGain, holdingYears, personalTaxRate = 0.42, hebesatz = 410) {
  if (capitalGain <= 0) return 0;

  const entity = entityTypes[entityType];
  if (!entity) return capitalGain * personalTaxRate;

  // Check Spekulationsfrist
  if (entity.spekulationsfrist && holdingYears >= (entity.spekulationsfristYears || 10)) {
    return 0; // Tax-free after holding period
  }

  // Within Spekulationsfrist or no Spekulationsfrist
  switch (entityType) {
    case 'private':
    case 'gmbh_co_kg': {
      const est = capitalGain * personalTaxRate;
      const soli = calculateSolidaritaetszuschlag(est);
      return est + soli;
    }
    case 'gmbh':
      return calculateCorporateTax('gmbh', capitalGain, hebesatz).total;
    case 'immo_gmbh':
      return capitalGain * baseTaxRates.kstPlusSoli;
    case 'stiftung':
      return capitalGain * baseTaxRates.kstPlusSoli;
    default:
      return capitalGain * personalTaxRate;
  }
}

// Get standard AfA rate based on year built
export function getAfARate(yearBuilt, method = 'standard') {
  if (method === 'degressive') {
    return degressiveAfA.annualRate; // 5%
  }

  if (method === 'denkmalschutz') {
    return denkmalschutzAfA.rental[0].annualRate; // 9% for years 1-8
  }

  // Standard linear AfA
  for (const tier of standardAfATiers) {
    const completionBefore = tier.completionBefore || Infinity;
    const completionFrom = tier.completionFrom || 0;
    if (yearBuilt >= completionFrom && yearBuilt < completionBefore) {
      return tier.annualRate;
    }
  }

  return 0.02; // Default fallback
}

// §15a EStG: Loss offset limitation for GmbH & Co. KG (Kommanditist)
// Losses are only deductible up to the positive capital account balance
export function applySection15aLimit(income, kommanditeinlage, capitalAccount) {
  if (income >= 0) {
    // Profit: no limitation, capital account increases
    return {
      deductibleLoss: 0,
      carriedLoss: 0,
      capitalAccount: capitalAccount + income,
    };
  }

  // Loss scenario
  const loss = Math.abs(income);
  const deductibleLoss = Math.min(loss, Math.max(0, capitalAccount));
  const carriedLoss = loss - deductibleLoss;
  const newCapitalAccount = Math.max(capitalAccount + income, -kommanditeinlage);

  return { deductibleLoss, carriedLoss, capitalAccount: newCapitalAccount };
}

// GewSt for short-term rental (private entity with commercial activity)
// Private Vermieter with hotel-like services triggers Gewerbesteuerpflicht
// Freibetrag §11 Abs. 1 Nr. 1 GewStG: €24,500 for natural persons/Personengesellschaften
export function calculateShortTermGewSt(income, hebesatz = 410) {
  if (income <= 24500) return { gewst: 0, estAnrechnung: 0, steuermessbetrag: 0 };

  const gewerbeertrag = income - 24500;
  const steuermessbetrag = gewerbeertrag * gewerbesteuerData.steuermesszahl;
  const gewst = steuermessbetrag * (hebesatz / 100);

  // §35 EStG: ESt credit = 4× Steuermessbetrag, capped at actual GewSt
  const estAnrechnung = Math.min(steuermessbetrag * 4.0, gewst);

  return { gewst, estAnrechnung, steuermessbetrag };
}

// Calculate annual depreciation amount
export function calculateDepreciation(depreciableBasis, yearBuilt, method = 'standard', year = 1, previousBookValue = null) {
  if (method === 'degressive') {
    // Declining balance: 5% of residual book value, switch to linear when more favorable
    const basis = previousBookValue !== null ? previousBookValue : depreciableBasis;
    const degressiveAmount = basis * degressiveAfA.annualRate;

    // Calculate equivalent linear amount for remaining useful life
    const usefulLifeYears = Math.round(1 / degressiveAfA.switchToLinearRate); // ~33 years for post-2022
    const remainingYears = Math.max(1, usefulLifeYears - year + 1);
    const linearAmount = basis / remainingYears;

    return Math.max(degressiveAmount, linearAmount);
  }

  if (method === 'denkmalschutz') {
    // §7i: 9% years 1-8, 7% years 9-12, based on renovation costs
    for (const period of denkmalschutzAfA.rental) {
      if (year >= period.yearsFrom && year <= period.yearsTo) {
        return depreciableBasis * period.annualRate;
      }
    }
    return 0; // After year 12, fully depreciated
  }

  // Standard linear
  const rate = getAfARate(yearBuilt, 'standard');
  return depreciableBasis * rate;
}
